import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { tryRefreshToken } from '../../api/client';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
}

const ERROR_MESSAGES: Record<number, string> = {
  401: 'Session expired',
  403: 'Access denied',
  404: 'Image not found',
  500: 'Server error',
};

export function AuthenticatedImage({ src, alt, className, fallback }: AuthenticatedImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      if (!src || !token) {
        setLoading(false);
        setErrorMsg(!src ? 'No image source' : 'Not authenticated');
        return;
      }

      try {
        setLoading(true);
        setErrorMsg(null);

        let response = await fetch(src, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // On 401, attempt token refresh and retry
        if (response.status === 401) {
          const refreshed = await tryRefreshToken();
          if (!refreshed) throw new Error('Session expired');

          // Get the refreshed token from the store
          const newToken = useAuthStore.getState().token;
          if (!newToken) throw new Error('Session expired');

          response = await fetch(src, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
        }

        if (!response.ok) {
          throw new Error(ERROR_MESSAGES[response.status] || `Error (${response.status})`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setErrorMsg(err instanceof Error ? err.message : 'Failed to load image');
          setLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, token]);

  if (loading) {
    return (
      <div className={`bg-gray-100 animate-pulse flex items-center justify-center ${className}`}>
        <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (errorMsg || !blobUrl) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className={`px-2 py-1 bg-gray-100 flex items-center justify-center text-gray-400 ${className}`}>
        <span className="text-sm text-center">{errorMsg || 'Failed to load image'}</span>
      </div>
    );
  }

  return <img src={blobUrl} alt={alt} className={className} />;
}
